import { ApiConfig } from 'bimplus-websdk';
import { DomainBuilder } from '../domain-builder.interface';
import { Project } from '../../project/project.model';

export class ProjectModel implements Project {
  id = '';
  name = '';
  teamSlug = '';
  teamName: string | undefined = undefined;
  rights = {};
  modelsCount = 0;
  tasksCount = 0;
}

export class ProjectBuilder implements DomainBuilder {
  private readonly project: Project;

  constructor() {
    this.project = new ProjectModel();
  }

  withId(id: ProjectModel['id']): this {
    this.project.id = id ?? '';
    return this;
  }

  withName(name: ProjectModel['name']): this {
    this.project.name = name ?? '';
    return this;
  }

  withTeamSlug(slug: ProjectModel['teamSlug']): this {
    this.project.teamSlug = slug ?? '';
    return this;
  }

  withRights(rights: ProjectModel['rights']): this {
    this.project.rights = rights ?? {};
    return this;
  }

  withTeamName(teamName: ProjectModel['teamName']): this {
    this.project.teamName = teamName ?? undefined;
    return this;
  }

  withThumbnailUrl(data: unknown, apiConfig: ApiConfig): this {
    (this.project as ProjectModel & { thumbnailUrl?: string }).thumbnailUrl =
      this.buildThumbnailUrl(data, apiConfig.protocol, apiConfig.host, apiConfig.version);
    return this;
  }

  withModelsCount(data: unknown): this {
    (this.project as ProjectModel).modelsCount = this.extractModelCount(data);
    return this;
  }

  withTasksCount(data: unknown): this {
    (this.project as ProjectModel).tasksCount = this.extractTasksCount(data);
    return this;
  }

  build(): Project {
    return this.project;
  }

  /**
   * Builds the full thumbnail URL for a project.
   *
   * The SDK's Project type omits the `thumbnail` field even though the API returns it.
   * This method casts the project to access that field and constructs a full URL from
   * the currently configured API base.
   *
   * @param project - The project object from the API.
   * @returns Full thumbnail URL, or undefined if no thumbnail path is present.
   */
  private buildThumbnailUrl(
    data: unknown,
    protocol: string,
    host: string,
    apiConfigVersion = 'v2'
  ): string | undefined {
    const project = data as Project;
    const thumbnail = (project as Project & { thumbnail?: string }).thumbnail;
    if (!thumbnail) {
      // Preserve an already-mapped thumbnailUrl (e.g. when re-mapping a domain Project).
      return (data as Record<string, unknown>)['thumbnailUrl'] as string | undefined;
    }
    const version = apiConfigVersion.replace(/\/$/, '');
    const path = thumbnail.startsWith('/') ? thumbnail : `/${thumbnail}`;
    const cacheBuster =
      (project as Project & { modified?: string | number }).modified ?? project.id;
    const cacheParam = cacheBuster ? `?${encodeURIComponent(String(cacheBuster))}` : '';
    return `${protocol}${host}/${version}${path}${cacheParam}`;
  }

  /**
   * Extracts the model count for a project.
   *
   * The SDK's Project type omits the `modelCount` field even though the API returns it.
   *
   * @param project - The project object from the API.
   * @returns The model count, or undefined if not present.
   */
  private extractModelCount(data: unknown): number {
    return (data as Project & { modelCount?: number }).modelCount ?? 0;
  }

  private extractTasksCount(data: unknown): number {
    return (data as Project & { topicCount?: number }).topicCount ?? 0;
  }
}
